// REVIEW CENTER 360 — every relationship with active cover, grouped by review state (v603 rules).
import { useMemo } from 'react';
import { reviewStatus, isActivePolicy, annualNow, type Person } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Avatar, Empty } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { waLink } from '../core/links.ts';
import { openers } from './forms.tsx';

export function ReviewsView() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const groups = useMemo(() => {
    const g: Record<'overdue' | 'never' | 'due_soon' | 'ok', { p: Person; rs: ReturnType<typeof reviewStatus>; annual: number }[]> = { overdue: [], never: [], due_soon: [], ok: [] };
    for (const p of view.rows('persons')) {
      if (p.archivedAt || p.stage === 'dependent') continue;
      const pols = view.where('policies', 'personId', p.id);
      const rs = reviewStatus(p, pols, today);
      if (rs.state === 'not_applicable') continue;
      const annual = pols.filter(isActivePolicy).reduce((s, x) => s + annualNow(view, x, today), 0);
      g[rs.state].push({ p, rs, annual });
    }
    g.overdue.sort((a, b) => b.rs.daysOverdue - a.rs.daysOverdue);
    g.never.sort((a, b) => b.annual - a.annual);
    g.due_soon.sort((a, b) => (a.rs.dueDate ?? '').localeCompare(b.rs.dueDate ?? ''));
    g.ok.sort((a, b) => (a.rs.dueDate ?? '').localeCompare(b.rs.dueDate ?? ''));
    return g;
  }, [view, today]);
  const cols: ['overdue' | 'never' | 'due_soon' | 'ok', string][] = [['overdue', 'review.due_now'], ['never', 'review.never'], ['due_soon', 'review.due_soon'], ['ok', 'review.ok']];
  return (
    <div>
      <div className="page-head"><div><h1>{t('review.title')}</h1><p>{t('review.subtitle')}</p></div></div>
      <div className="grid grid-2">
        {cols.map(([k, label]) => (
          <section key={k} className="card" data-testid={`review-${k}`}>
            <div className="card-head"><h3>{t(label)}</h3><span className={`chip ${k === 'overdue' ? 'crit' : k === 'never' ? 'high' : k === 'ok' ? 'ok' : 'gold'}`}>{groups[k].length}</span></div>
            {groups[k].length ? <div className="agenda-list">{groups[k].slice(0, 60).map(({ p, rs, annual }) => {
              const first = p.firstName || p.displayName.split(' ')[0];
              const invite = t('template.review_invite', { first, signature: rt.advisor.signature || rt.advisor.name || '' });
              return (
                <div key={p.id} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }}>
                  <Avatar name={p.displayName} size="sm" />
                  <div style={{ minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(personPath(p.id, 'reviews'))}>
                    <div className="t person-name one-line">{p.displayName}</div>
                    <div className="s">{[rs.lastReviewDate ? t('review.last', { date: i18n.date(rs.lastReviewDate) }) : '', rs.dueDate ? t('review.due', { date: i18n.date(rs.dueDate) }) : '', i18n.money(annual)].filter(Boolean).join(' · ')}</div>
                  </div>
                  <div className="row">
                    {p.primaryPhone && k !== 'ok' && <a className="btn btn-sm btn-icon" href={waLink(p.primaryPhone, invite)} target="_blank" rel="noreferrer" aria-label={t('common.whatsapp')}><Icon name="chat" /></a>}
                    <button className="btn btn-sm" onClick={() => openers(rt).review(p.id)}>{t('review.complete')}</button>
                  </div>
                </div>
              );
            })}</div> : <Empty icon="review">{t('review.empty')}</Empty>}
          </section>
        ))}
      </div>
    </div>
  );
}
